import { useMemo, useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Button,
  ColorScheme,
  Modal,
  Select,
  Stack,
  Text,
  useClipboard,
} from '@hivewatch/dark'

import {
  useLinkCustomerSecretProviderMutation,
  useListCustomerSecretProviderQuery,
} from '@/graphql/generated/hooks'
import { CredentialLinkEntityType } from '@/graphql/generated/schemas'
import { MODAL_Z_INDEX } from '@/styles/theme'

interface DarkLinkSecretProviderModalIProps {
  colorScheme: ColorScheme
  entityId: string
  entityType: CredentialLinkEntityType
  isOpen: boolean
  onClose: () => void
  refetchQueryName: string
  secretTemplate: string
}

export const DarkLinkSecretProviderModal = ({
  colorScheme,
  entityId,
  entityType,
  isOpen,
  onClose,
  refetchQueryName,
  secretTemplate,
}: DarkLinkSecretProviderModalIProps) => {
  const { t } = useTranslation()
  const { showError } = useToasts()
  const [secretArn, setSecretArn] = useState<string | null>(null)
  const [selectedProviderId, setSelectedProviderId] = useState('')
  const { data } = useListCustomerSecretProviderQuery()
  const providers =
    data?.customerSecretProviders?.edges
      ?.map((edge) => edge?.node)
      .filter(Boolean) ?? []
  const providerOptions = useMemo(
    () =>
      providers.map((provider) => ({
        label: provider.providerName,
        value: provider.id,
      })),
    [providers]
  )
  const selectedProvider =
    providerOptions.find((option) => option.value === selectedProviderId) ??
    null
  const { copied: hasCopiedArn, copy: copyArn } = useClipboard({
    value: secretArn ?? '',
  })
  const { copied: hasCopiedTemplate, copy: copyTemplate } = useClipboard({
    value: secretTemplate,
  })
  const [linkCustomerSecretProvider, { loading: isLoading }] =
    useLinkCustomerSecretProviderMutation()

  const handleClose = () => {
    if (isLoading) return
    setSecretArn(null)
    setSelectedProviderId('')
    onClose()
  }

  const handleLink = async () => {
    if (!selectedProviderId) return

    try {
      const result = await linkCustomerSecretProvider({
        variables: {
          input: {
            customerSecretProviderId: selectedProviderId,
            entityId,
            entityType,
          },
        },
        refetchQueries: [refetchQueryName],
      })
      setSecretArn(
        result.data?.linkCustomerSecretProvider?.customerSecretProviderLink
          ?.entity?.customerSecretProvider?.secretArn ?? null
      )
    } catch (error) {
      showError(t('devices:crud_secret_link_error'))
      Sentry.captureException(
        error ?? new Error(t('devices:crud_secret_link_error'))
      )
    }
  }

  const codeBg =
    colorScheme === 'light' ? 'light.bg.card-elevated' : 'bg.card-elevated'
  const codeBorder =
    colorScheme === 'light' ? 'light.border.subtle' : 'border.subtle'

  return (
    <Modal
      closeOnEsc={!isLoading}
      closeOnOverlayClick={!isLoading}
      colorScheme={colorScheme}
      isCloseHidden={isLoading}
      isOpen={isOpen}
      isSaveDisabled={!secretArn && !selectedProviderId}
      isSaveLoading={isLoading}
      maxW='620px'
      onCancel={handleClose}
      onClose={handleClose}
      onSave={secretArn ? handleClose : handleLink}
      saveButtonLabel={
        secretArn ? t('common:done') : t('devices:crud_secret_associate_button')
      }
      showCancelButton={!secretArn && !isLoading}
      size='compact'
      title={
        secretArn
          ? t('devices:crud_secret_associated_title')
          : t('devices:crud_secret_associate_title')
      }
      zIndex={MODAL_Z_INDEX + 1}
    >
      {secretArn ? (
        <Stack gap='16px'>
          <Box>
            <Text fontSize='14px' fontWeight='600'>
              {t('devices:crud_secret_create_aws')}
            </Text>
            <Text fontSize='12px' mt='4px' opacity={0.7}>
              {t('devices:crud_secret_create_aws_description')}
            </Text>
          </Box>

          <CodeBlock
            bg={codeBg}
            borderColor={codeBorder}
            colorScheme={colorScheme}
            copied={hasCopiedArn}
            copyLabel={t('devices:crud_secret_copy_arn')}
            onCopy={copyArn}
            value={secretArn}
          />

          <Box>
            <Text fontSize='14px' fontWeight='600'>
              {t('devices:crud_secret_json_structure')}
            </Text>
            <Text fontSize='12px' mt='4px' opacity={0.7}>
              {t('devices:crud_secret_json_description')}
            </Text>
          </Box>
          <CodeBlock
            bg={codeBg}
            borderColor={codeBorder}
            colorScheme={colorScheme}
            copied={hasCopiedTemplate}
            copyLabel={t('devices:crud_secret_copy_json')}
            onCopy={copyTemplate}
            value={secretTemplate}
          />
        </Stack>
      ) : (
        <Stack gap='16px'>
          <Text fontSize='14px'>
            {t('devices:crud_secret_select_description')}
          </Text>
          {providerOptions.length === 0 ? (
            <Alert
              colorScheme={colorScheme}
              description={t('devices:crud_secret_none')}
              variant='info'
            />
          ) : (
            <Select
              colorScheme={colorScheme}
              onChange={(option) => setSelectedProviderId(option?.value ?? '')}
              options={providerOptions}
              placeholder={t('devices:crud_secret_select_placeholder')}
              value={selectedProvider}
            />
          )}
        </Stack>
      )}
    </Modal>
  )
}

interface CodeBlockIProps {
  bg: string
  borderColor: string
  colorScheme: ColorScheme
  copied: boolean
  copyLabel: string
  onCopy: () => void
  value: string
}

const CodeBlock = ({
  bg,
  borderColor,
  colorScheme,
  copied,
  copyLabel,
  onCopy,
  value,
}: CodeBlockIProps) => {
  const { t } = useTranslation()

  return (
    <Box>
      <Box
        bg={bg}
        border='1px solid'
        borderColor={borderColor}
        fontFamily='monospace'
        fontSize='12px'
        overflowX='auto'
        p='12px'
        whiteSpace='pre'
      >
        {value}
      </Box>
      <Button
        colorScheme={colorScheme}
        onClick={onCopy}
        size='sm'
        variant='secondary'
      >
        {copied ? t('common:copied') : copyLabel}
      </Button>
    </Box>
  )
}
